/**
 * router/routes/deposito.routes.ts
 * The Depósito route tree (odd/tasks/compras-deposito.md S1, L5), mounted once
 * per module: compras.routes.ts and servicios.routes.ts each call
 * depositoRoutes() with their own base. Same components, same child paths and
 * same per-tab capabilities under both; only the route names and
 * meta.depositoBase differ, so each mount keeps its module context (the
 * sidebar scopes items by path prefix) and its links stay inside it.
 */
import type { RouteComponent, RouteRecordRaw } from 'vue-router';
import { DEPOSITO_ITEM_CAPABILITY, DEPOSITO_TABS, type DepositoTab } from '../../modules/compras/deposito/deposito-tabs.js';
import { depositoBasePath, depositoRouteName, type DepositoBase } from '../../modules/compras/deposito/deposito-base.js';

// Module-level loaders, so both mounts share the very same component records.
const DepositoLayout = () => import('../../views/compras/deposito/DepositoLayout.vue');
const ComprasStockItemView = () => import('../../views/compras/ComprasStockItemView.vue');

const TAB_COMPONENTS: Record<DepositoTab['value'], () => Promise<RouteComponent>> = {
  inicio: () => import('../../views/compras/deposito/DepositoInicioView.vue'),
  recibir: () => import('../../views/compras/deposito/DepositoRecibirView.vue'),
  entregar: () => import('../../views/compras/deposito/DepositoEntregarView.vue'),
  contar: () => import('../../views/compras/deposito/DepositoContarView.vue'),
  mover: () => import('../../views/compras/deposito/DepositoMoverView.vue'),
  movimientos: () => import('../../views/compras/deposito/DepositoMovimientosView.vue'),
};

/** The layout with its tabs, plus the item view, under `/<base>/deposito`. Paths are relative to the tenant shell. */
export function depositoRoutes(base: DepositoBase, extraChildren: RouteRecordRaw[] = []): RouteRecordRaw[] {
  const basePath = depositoBasePath(base).slice(1);
  return [
    {
      path: basePath,
      component: DepositoLayout,
      meta: { depositoBase: base },
      children: [
        ...DEPOSITO_TABS.map((tab) => ({
          path: tab.path,
          name: depositoRouteName(base, tab.value),
          component: TAB_COMPONENTS[tab.value],
          meta: { requiresCapability: tab.requiredCapability },
        })),
        ...extraChildren,
      ],
    },
    {
      path: `${basePath}/stock/:warehouseId/:itemMasterId`,
      name: depositoRouteName(base, 'item'),
      component: ComprasStockItemView,
      props: true,
      meta: { requiresCapability: DEPOSITO_ITEM_CAPABILITY, depositoBase: base },
    },
  ];
}
